import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { launchDarklyClient } from '../../services/launchdarkly';
import {
  FEATURE_FLAGS,
  LAUNCHDARKLY_CONFIG,
} from '../../services/launchdarkly/constants';
import Auth from '../../Utils/Auth/auth';

const LaunchDarklyContext = createContext();

const LaunchDarklyProvider = ({ children }) => {
  const [instoreModuleV2Enabled, setInstoreModuleV2Enabled] = useState(false);

  const checkFeatureFlag = async (flagKey, defaultValue = false) => {
    try {
      const client = launchDarklyClient.getClient();
      if (!client) {
        console.warn(
          'LaunchDarkly client not available, returning default value'
        );
        return defaultValue;
      }
      const currentContext = client.getContext();

      const rawValue = client.variation(flagKey, defaultValue);
      let value;
      if (rawValue === 'true') {
        value = true;
      } else if (rawValue === 'false') {
        value = false;
      } else {
        value = rawValue;
      }
      console.log(
        `Feature flag ${flagKey} value:`,
        value,
        'for context:',
        currentContext
      );

      return value;
    } catch (error) {
      console.error(`Error checking feature flag ${flagKey}:`, error);
      return defaultValue;
    }
  };

  /**
   * Check if the feature flags are enabled
   * @returns {Promise<boolean>} Promise that resolves to true if enabled
   */
  const loadFlags = async () => {
    const isEnabled = await checkFeatureFlag(
      FEATURE_FLAGS.INSTORE_MODULE_V2,
      false
    );
    setInstoreModuleV2Enabled(isEnabled);
    return isEnabled;
  };

  /**
   * Initialize LaunchDarkly client
   * @returns {boolean} True if initialized, false otherwise
   */
  const initializeLaunchDarkly = async () => {
    try {
      const storeContext = loadContext();

      await launchDarklyClient.initialize(storeContext);
      return true;
    } catch (error) {
      console.error('Failed to initialize LaunchDarkly with store:', error);
      return false;
    }
  };

  /**
   * Close LaunchDarkly client
   */
  const closeLaunchDarkly = async () => {
    try {
      await launchDarklyClient.close();
    } catch (error) {
      console.error('Failed to close LaunchDarkly client:', error);
    }
  };

  /**
   * Load the context for the LaunchDarkly client
   * @returns {Object} The context for the LaunchDarkly client
   */
  const loadContext = () => {
    const { employee } = Auth.userData();
    if (employee) {
      return {
        kind: 'user',
        key: employee.employee_id,
      };
    }

    return LAUNCHDARKLY_CONFIG.ANONYMOUS_USER;
  };

  useEffect(() => {
    if (Auth.userLoggedIn()) {
      initializeLaunchDarkly();
    }
  }, []);

  return (
    <LaunchDarklyContext.Provider
      value={{
        initializeLaunchDarkly,
        closeLaunchDarkly,
        checkFeatureFlag,
        loadFlags,
        instoreModuleV2Enabled,
      }}
    >
      {children}
    </LaunchDarklyContext.Provider>
  );
};

LaunchDarklyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useLaunchDarkly = () => {
  const context = useContext(LaunchDarklyContext);
  if (!context) {
    throw new Error('useLaunchDarkly must be used within LaunchDarklyProvider');
  }
  return context;
};

export { LaunchDarklyProvider, useLaunchDarkly };
